import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFamilyAuth } from '../../contexts/FamilyAuthContext';
import axios from 'axios';

/**
 * Phase 17: Step 2 - Household Setup (Admin Only)
 * Update household name and picture
 */
function Step2Household() {
  const navigate = useNavigate();
  const { household, getAuthHeader } = useFamilyAuth();
  const [householdName, setHouseholdName] = useState('');
  const [saving, setSaving] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    if (household) {
      setHouseholdName(household.name || '');
    }
  }, [household]);

  const handleNext = async () => {
    if (!householdName.trim()) {
      alert('Bitte geben Sie einen Haushaltsnamen ein.');
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/family/household`,
        { name: householdName },
        { headers: getAuthHeader() }
      );
      navigate('/onboarding/step3');
    } catch (error) {
      console.error('Error updating household:', error);
      alert('Fehler beim Aktualisieren des Haushalts.');
      setSaving(false);
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/step3');
  };

  return (
    <div className="w-full max-w-2xl mx-auto animate-fade-in">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex justify-center gap-2">
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white"></div>
          <div className="w-3 h-3 rounded-full bg-white/50"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
          <div className="w-3 h-3 rounded-full bg-white/30"></div>
        </div>
      </div>

      {/* Card */}
      <div className="bg-neutral-900/90 backdrop-blur-xl rounded-3xl border border-neutral-600/40 p-8 sm:p-12 shadow-2xl animate-scale-in">
        {/* Icon */}
        <div className="text-center mb-6">
          <span className="text-7xl">🏠</span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#F9FAFB] mb-2 text-center">
          Ihr Haushalt
        </h1>
        <p className="text-neutral-500 mb-8 text-center">
          Geben Sie Ihrem Haushalt einen Namen
        </p>

        {/* Form */}
        <div className="space-y-6 mb-8">
          <div>
            <label className="block text-sm font-semibold text-[#CBD5F5] mb-2">
              Haushaltsname
            </label>
            <input
              type="text"
              value={householdName}
              onChange={(e) => setHouseholdName(e.target.value)}
              placeholder="z.B. Familie Müller"
              className="w-full px-6 py-4 border-2 border-hayat-neutral-200 rounded-2xl focus:outline-none focus:border-hayat-primary-500 transition-colors text-lg"
              disabled={saving}
            />
          </div>

          {/* Picture Upload Placeholder */}
          <div>
            <label className="block text-sm font-semibold text-[#CBD5F5] mb-2">
              Familienfoto (optional)
            </label>
            <div className="border-2 border-dashed border-hayat-neutral-300 rounded-2xl p-8 text-center text-hayat-neutral-500 hover:border-hayat-primary-400 transition-colors cursor-pointer">
              <span className="text-4xl mb-2 block">📷</span>
              <p className="text-sm">Foto hochladen (bald verfügbar)</p>
            </div>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-4">
          <button
            onClick={handleSkip}
            disabled={saving}
            className="flex-1 py-4 bg-hayat-neutral-100 text-[#CBD5F5] rounded-2xl font-semibold hover:bg-hayat-neutral-200 active:scale-95 transition-all duration-300 disabled:opacity-50"
          >
            Überspringen
          </button>
          <button
            onClick={handleNext}
            disabled={saving || !householdName.trim()}
            className="flex-1 py-4 bg-gradient-to-r from-hayat-primary-500 to-hayat-primary-600 text-white rounded-2xl font-bold shadow-hayat-lg hover:shadow-hayat-xl hover:scale-105 active:scale-95 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Wird gespeichert...' : 'Weiter →'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Step2Household;